import { type ChipColorTypes } from '@saviynt/design-system';
import { defineMessages } from 'react-intl';

// Common
export const managePageMessages = defineMessages({
  columnKeyName: { id: '8ZDCh9', defaultMessage: 'NAME' },
  columnKeyDesc: { id: '4WcB2q', defaultMessage: 'DESCRIPTION' },
  columnKeyPlatform: { id: 'BHKmk6', defaultMessage: 'PLATFORM' },
  columnKeyLlmModel: { id: 'mUe8Yb', defaultMessage: 'LLM MODEL' },
  columnKeyStatus: { id: 'a1fAXn', defaultMessage: 'STATUS' },
  columnKeyCriticality: { id: 'e8kxzb', defaultMessage: 'CRITICALITY' },
  columnKeyBuOwner: { id: 'gyanIN', defaultMessage: 'BUSINESS OWNER' },
  columnKeyCreatedOn: { id: 'nN5XzC', defaultMessage: 'CREATED ON' },
  fKeyPlatform: { id: 'Mmuj1R', defaultMessage: 'Platform' },
  fKeyOwners: { id: 'srCJeL', defaultMessage: 'Owners' },
  fKeyLabels: { id: 'm3QEyd', defaultMessage: 'Labels' },
  fKeyStatus: { id: 'tzMNF3', defaultMessage: 'Status' },
  sortKeys: { id: '6zbn8c', defaultMessage: 'KEYS' },
  sortValues: { id: 'TauGxb', defaultMessage: 'VALUES' },
  sortNameType: { id: '5FnhML', defaultMessage: 'NAME & EMAIL' },
  sortSource: { id: 'peHnYD', defaultMessage: 'SOURCE' },
  sortRole: { id: 'vgwBJH', defaultMessage: 'ROLE' },
  sortRank: { id: '9weXJd', defaultMessage: 'RANK' },
  roleBusiness: { id: 'w1Fanr', defaultMessage: 'Business' },
  roleTechnical: { id: 'tUCXwz', defaultMessage: 'Technical' },
  critical: { id: '2pzTGC', defaultMessage: 'Critical' },
  high: { id: 'AxMhQr', defaultMessage: 'High' },
  medium: { id: 'ovJ26C', defaultMessage: 'Medium' },
  low: { id: '477I0g', defaultMessage: 'Low' },
  rank1: { id: 'Tf9Oo0', defaultMessage: '1' },
  rank2: { id: 'QLJEES', defaultMessage: '2' },
  rank3: { id: 'O6+/CI', defaultMessage: '3' },
  sourceChipPolicyMsg: {
    id: 'iJEMlO',
    defaultMessage: 'Assigned automatically by policy',
  },
  sourceChipApiMsg: { id: 'JN3Aaw', defaultMessage: 'Assigned via API' },
  sourceChipManualMsg: {
    id: 'sm4bN4',
    defaultMessage: 'Assigned manually in the UI',
  },
  sourceChipIngestionMsg: {
    id: 'fl1dNo',
    defaultMessage: 'Assigned during ingestion from connector',
  },
  labelsCrtMsg1: {
    id: 'kLU5BF',
    defaultMessage: 'This required field is incomplete',
  },
  labelsCrtMsg2: {
    id: '7rsj9p',
    defaultMessage:
      'Invalid format. Only use lowercase letters, numbers, hyphens (-), or underscores (_)',
  },
  labelsCrtMsg3: {
    id: 'WjPzb1',
    defaultMessage:
      'Invalid format. Only a-z, 0-9, hyphens (-), and underscores (_) are allowed, and the value must contain at least one lowercase letter.',
  },
  labelsCrtMsg4: {
    id: 'ClH5o8',
    defaultMessage: 'Invalid length. Maximum 255 characters allowed.',
  },
});

export type ManagePageMessageKey = keyof typeof managePageMessages;

export const LIMIT_CALL = 25;
export const FILTER_OPTIONS_LIMIT = 100;

// Search, Filters, and Sort
export const CHAR_LIMIT_FOR_SEARCH = 2;
export const F_KEY_CF = 'platforms';
export const F_KEY_OI = 'ownerIds';
export const F_KEY_LB = 'labels';
export const F_KEY_ST = 'status';
export const FILTERS_CONFIG: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
}[] = [
  {
    messageLabelKey: 'fKeyPlatform',
    value: F_KEY_CF,
  },
  {
    messageLabelKey: 'fKeyOwners',
    value: F_KEY_OI,
  },
  {
    messageLabelKey: 'fKeyLabels',
    value: F_KEY_LB,
  },
  {
    messageLabelKey: 'fKeyStatus',
    value: F_KEY_ST,
  },
];

// Lists
export const LIST_COLUMNS: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
  isSortField: boolean;
}[] = [
  {
    messageLabelKey: 'columnKeyName',
    value: 'name',
    isSortField: true,
  },
  {
    messageLabelKey: 'columnKeyDesc',
    value: 'description',
    isSortField: false,
  },
  {
    messageLabelKey: 'columnKeyPlatform',
    value: 'platform',
    isSortField: false,
  },
  {
    messageLabelKey: 'columnKeyLlmModel',
    value: 'llmModels',
    isSortField: false,
  },
  {
    messageLabelKey: 'columnKeyStatus',
    value: 'status',
    isSortField: false,
  },
  {
    messageLabelKey: 'columnKeyCriticality',
    value: 'criticality',
    isSortField: true,
  },
  {
    messageLabelKey: 'columnKeyBuOwner',
    value: 'ownerIds',
    isSortField: false,
  },
  {
    messageLabelKey: 'columnKeyCreatedOn',
    value: 'createdAt',
    isSortField: true,
  },
];

interface ApplicationLogoConfig {
  match: (platform: string) => boolean;
  logo: string;
}

export const PLATFORM_CONFIG: ApplicationLogoConfig[] = [
  { match: (p) => p.includes('copilot'), logo: 'MicrosoftCopilotStudio' },
  { match: (p) => p.includes('foundry'), logo: 'MicrosoftFoundry' },
  { match: (p) => p.includes('vertex'), logo: 'GoogleVertexAI' },
  { match: (p) => p.includes('gemini'), logo: 'GoogleGemini' },
  { match: (p) => p.includes('bedrock'), logo: 'AmazonBedrock' },
  { match: (p) => p.includes('agentcore'), logo: 'AmazonAgentcore' },
  { match: (p) => p.includes('agentforce'), logo: 'Salesforce' },
  { match: (p) => p.includes('servicenow'), logo: 'ServiceNow' },
  { match: (p) => p.includes('aws'), logo: 'AWS' },
  { match: (p) => p.includes('azure'), logo: 'Azure' },
  { match: (p) => p.includes('gcp'), logo: 'GCP' },
];

export const MODEL_CONFIG: ApplicationLogoConfig[] = [
  { match: (m) => m.includes('claude'), logo: 'AnthropicClaude' },
  { match: (m) => m.includes('gpt'), logo: 'OpenAIGPT' },
  { match: (m) => m.includes('gemini'), logo: 'GoogleGemini' },
  { match: (m) => m.includes('llama'), logo: 'MetaLlama' },
  { match: (m) => m.includes('mistral'), logo: 'Mistral' },
  { match: (m) => m.includes('qwen'), logo: 'AlibabaQwen' },
  { match: (m) => m.includes('grok'), logo: 'Grok' },
  { match: (m) => m.includes('deepseek'), logo: 'Deepestseek' },
  { match: (m) => m.includes('openai'), logo: 'MicrosoftAzureOpenAI' },
];

// Sidepanel overview
export const CRITICAL_OPTIONS: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
}[] = [
  { messageLabelKey: 'critical', value: 'critical' },
  { messageLabelKey: 'high', value: 'high' },
  { messageLabelKey: 'medium', value: 'medium' },
  { messageLabelKey: 'low', value: 'low' },
];

// Sidepanel labels
export const LABEL_COUNT_LIMIT = 15;
export const LABEL_TABLE_COLUMN_KEY_1 = 'keys';
export const LABEL_TABLE_COLUMN_KEY_2 = 'values';
export const LABEL_TABLE_COLUMN_CONFIG: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
  key: string;
}[] = [
  {
    key: LABEL_TABLE_COLUMN_KEY_1,
    value: LABEL_TABLE_COLUMN_KEY_1,
    messageLabelKey: 'sortKeys',
  },
  {
    key: LABEL_TABLE_COLUMN_KEY_2,
    value: LABEL_TABLE_COLUMN_KEY_2,
    messageLabelKey: 'sortValues',
  },
];

// Sidepanel owners
export const OWNERS_COUNT_LIMIT = 20;
export const OWNER_TABLE_COLUMN_KEY_1 = 'name_type';
export const OWNER_TABLE_COLUMN_KEY_2 = 'source';
export const OWNER_TABLE_COLUMN_KEY_3 = 'role';
export const OWNER_TABLE_COLUMN_KEY_4 = 'rank';
export const OWNER_TABLE_COLUMN_CONFIG: {
  key: string;
  messageLabelKey: ManagePageMessageKey;
  value: string;
}[] = [
  {
    key: OWNER_TABLE_COLUMN_KEY_1,
    value: OWNER_TABLE_COLUMN_KEY_1,
    messageLabelKey: 'sortNameType',
  },
  {
    key: OWNER_TABLE_COLUMN_KEY_2,
    value: OWNER_TABLE_COLUMN_KEY_2,
    messageLabelKey: 'sortSource',
  },
  {
    key: OWNER_TABLE_COLUMN_KEY_3,
    value: OWNER_TABLE_COLUMN_KEY_3,
    messageLabelKey: 'sortRole',
  },
  {
    key: OWNER_TABLE_COLUMN_KEY_4,
    value: OWNER_TABLE_COLUMN_KEY_4,
    messageLabelKey: 'sortRank',
  },
];
export const EDIT_OWNER_TABLE_COLUMN_KEY_1 = 'name_type';
export const EDIT_OWNER_TABLE_COLUMN_KEY_2 = 'role';
export const EDIT_OWNER_TABLE_COLUMN_KEY_3 = 'rank';
export const EDIT_OWNER_TABLE_COLUMN_CONFIG: {
  messageLabelKey: ManagePageMessageKey;
  key: string;
  value: string;
}[] = [
  {
    key: EDIT_OWNER_TABLE_COLUMN_KEY_1,
    value: EDIT_OWNER_TABLE_COLUMN_KEY_1,
    messageLabelKey: 'sortNameType',
  },
  {
    key: EDIT_OWNER_TABLE_COLUMN_KEY_2,
    value: EDIT_OWNER_TABLE_COLUMN_KEY_2,
    messageLabelKey: 'sortRole',
  },
  {
    key: EDIT_OWNER_TABLE_COLUMN_KEY_3,
    value: EDIT_OWNER_TABLE_COLUMN_KEY_3,
    messageLabelKey: 'sortRank',
  },
];
export const EDIT_OWNERS_ROLE_OPTIONS: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
  secondaryLabel: string;
}[] = [
  {
    messageLabelKey: 'roleBusiness',
    value: 'Business Owner',
    secondaryLabel:
      "Accountable for the agent's overall purpose, risk, and business alignment.",
  },
  {
    messageLabelKey: 'roleTechnical',
    value: 'Technical Owner',
    secondaryLabel:
      'Responsible for technical setup, integrations, configuration, and ongoing maintenance of the agent.',
  },
];
export const EDIT_OWNERS_RANK: {
  messageLabelKey: ManagePageMessageKey;
  value: string;
}[] = [
  { messageLabelKey: 'rank1', value: '1' },
  { messageLabelKey: 'rank2', value: '2' },
  { messageLabelKey: 'rank3', value: '3' },
];

// Sidepanel shared
interface SourceConfig {
  match: (source: string) => boolean;
  color: ChipColorTypes;
  messageLabelKey: ManagePageMessageKey;
}

export const SOURCE_CONFIG: SourceConfig[] = [
  {
    match: (s) => s.includes('policy'),
    color: 'pink',
    messageLabelKey: 'sourceChipPolicyMsg',
  },
  {
    match: (s) => s.includes('api'),
    color: 'orange',
    messageLabelKey: 'sourceChipApiMsg',
  },
  {
    match: (s) => s.includes('manual'),
    color: 'neutral',
    messageLabelKey: 'sourceChipManualMsg',
  },
  {
    match: (s) => s.includes('ingestion'),
    color: 'brandPrimary',
    messageLabelKey: 'sourceChipIngestionMsg',
  },
];

interface StatusConfig {
  match: (status: string) => boolean;
  color: ChipColorTypes;
}

export const STATUS_CONFIG: StatusConfig[] = [
  { match: (s) => s === 'approved', color: 'success' },
  { match: (s) => s === 'active', color: 'brandPrimary' },
  { match: (s) => s === 'pending', color: 'warning' },
  { match: (s) => s === 'retired', color: 'neutral' },
  { match: (s) => s === 'review', color: 'info' },
  { match: (s) => s === 'suspended', color: 'critical' },
];

export const LABEL_CRITICAL_VALIDATION: {
  match: (v: string | null) => boolean;
}[] = [
  { match: (v) => v !== null && v.trim().length === 0 },
  { match: (v) => v !== null && !/^[a-z0-9_-]+$/.test(v.trim()) },
  { match: (v) => v !== null && !/[a-z]/.test(v.trim()) },
  { match: (v) => v !== null && v.trim().length >= 255 },
];

export const LABEL_CRITICAL_VALIDATION_MSG: {
  match: (v: string | null) => boolean;
  message: ManagePageMessageKey;
}[] = [
  {
    match: (v) => v !== null && v.trim().length === 0,
    message: 'labelsCrtMsg1',
  },
  {
    match: (v) => v !== null && !/^[a-z0-9_-]+$/.test(v.trim()),
    message: 'labelsCrtMsg2',
  },
  {
    match: (v) => v !== null && !/[a-z]/.test(v.trim()),
    message: 'labelsCrtMsg3',
  },
  {
    match: (v) => v !== null && v.trim().length >= 255,
    message: 'labelsCrtMsg4',
  },
];
